import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import AboutLocationContent from './AboutLocationContent'
import { connect } from 'dva'
import SidebarDetailHeader from '../SidebarDetailHeader'
import styles from './AboutLocation.module.scss'
import { STATES_DATA } from '../../../lib/constants'
import Copy from '../../../assets/svg/Feather/Copy'
import ghAnalytics from '../../../lib/analytics'

const TOAST_DURATION_MS = 6000

function AboutLocationSidebar(props) {
	const { assetsUrl, huntUnitsAtLocation, huntUnitsLoading, layersAtLocationData, locationLoading, availableCategories, stateName, dispatch, userProfile, featureAtLocation, hideCoords } = props
	const { county, layersAtLocation, overlaysAtLocation, point } = layersAtLocationData
	const resolvedStateName = stateName || (layersAtLocation.length > 0 ? layersAtLocation[0].layer.state : layersAtLocationData.state)
	const stateIcon = `${assetsUrl}/states/icons/small/${STATES_DATA[resolvedStateName]?.abbrev.toLowerCase()}.png`
	const toastTimerRef = useRef(null)

	let sidebarHeaderTitle = 'About this Location'
	if (county) {
		sidebarHeaderTitle = county
		if (resolvedStateName) {
			sidebarHeaderTitle += `, ${STATES_DATA[resolvedStateName].abbrev}`
		}
	}

	const lat = point[1].toFixed(6)
	const lon = point[0].toFixed(6)
	const coordsDisplay = hideCoords ? '--' : `${lat} , ${lon}`
	const coordsCopyText = hideCoords ? '' : `${lat}, ${lon}`

	const showCopiedToast = () => {
		ghAnalytics.logAboutLocationCoordinatesCopied()

		if (toastTimerRef.current) clearTimeout(toastTimerRef.current)

		dispatch({
			type: 'toast/show',
			payload: {
				placement: 'top',
				text: 'Coordinates copied!',
				icon: Copy,
				iconColor: '#FFFFFF',
				dismissable: true,
				onClick: () => dispatch({ type: 'toast/dismiss' }),
			},
		})

		toastTimerRef.current = setTimeout(() => {
			dispatch({ type: 'toast/dismiss' })
		}, TOAST_DURATION_MS)
	}

	const handleCopyCoords = () => {
		if (props.hideCoords || !coordsCopyText) return

		// Modern clipboard API (HTTPS / localhost)
		if (navigator.clipboard && navigator.clipboard.writeText) {
			navigator.clipboard.writeText(coordsCopyText)
				.then(showCopiedToast)
				.catch(() => fallbackCopy())
		} else {
			fallbackCopy()
		}
	}

	const fallbackCopy = () => {
		const textarea = document.createElement('textarea')
		textarea.value = coordsCopyText
		textarea.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0'
		document.body.appendChild(textarea)
		textarea.focus()
		textarea.select()
		try {
			const success = document.execCommand('copy')
			if (success) showCopiedToast()
		} catch (e) {
			// copy not supported
		}
		document.body.removeChild(textarea)
	}

	return (
		<>
			<SidebarDetailHeader
				title={locationLoading ? 'About this Location' : sidebarHeaderTitle}
				titleIcon={!locationLoading && resolvedStateName ? <img width='24' height='24' src={stateIcon} /> : null}
				style={{ paddingBottom: '16px' }}
			/>

			<button className={styles.locationBox} onClick={handleCopyCoords} title='Click to copy coordinates'>
				<div className={styles.coordsContent}>
					<span className={styles.coordsLabel}>Coordinates</span>
					<span className={styles.coordsValue}>{coordsDisplay}</span>
				</div>
				<span className={styles.copyIcon}><Copy width={22} color='#ffffff' /></span>
			</button>

	<AboutLocationContent
		locationLoading={locationLoading}
		huntUnitsLoading={huntUnitsLoading}
		layersAtLocation={layersAtLocation}
		huntUnitsAtLocation={huntUnitsAtLocation}
		overlaysAtLocation={overlaysAtLocation}
		availableCategories={availableCategories}
		stateName={resolvedStateName}
		assetsUrl={assetsUrl}
		dispatch={dispatch}
		userProfile={userProfile}
		featureAtLocation={featureAtLocation}
		hideCoords={hideCoords}
	/>
		</>
	)
}

AboutLocationSidebar.propTypes = {
	locationLoading: PropTypes.bool,
	assetsUrl: PropTypes.string,
	availableCategories: PropTypes.array,
	dispatch: PropTypes.func,
	featureAtLocation: PropTypes.object,
	hideCoords: PropTypes.bool,
	huntUnitsAtLocation: PropTypes.array,
	huntUnitsLoading: PropTypes.bool,
	layersAtLocationData: PropTypes.object,
	stateName: PropTypes.string,
	userProfile: PropTypes.object,
}

export default connect((model) => {
	const { layersByEvent, layersByState, layersAtLocationData } = model.layers
	const allLayers = [...layersByEvent, ...layersByState]

	// Resolve state name from location data, falling back to layer state or
	// case-insensitive match against loaded layers (handles geocode name variants)
	let resolvedStateName =
		layersAtLocationData.state ||
		(layersAtLocationData.layersAtLocation?.length > 0
			? layersAtLocationData.layersAtLocation[0].layer.state
			: null)

	// If exact match fails, try case-insensitive match against loaded layer states
	if (resolvedStateName) {
		const exactMatch = allLayers.find(s => s.stateName === resolvedStateName)
		if (!exactMatch) {
			const lowerName = resolvedStateName.toLowerCase()
			const looseMatch = allLayers.find(s => s.stateName.toLowerCase() === lowerName)
			if (looseMatch) resolvedStateName = looseMatch.stateName
		}
	}

	const stateLayerData = resolvedStateName
		? allLayers.find(s => s.stateName === resolvedStateName)
		: null
	const availableCategories = stateLayerData?.categories || []

	return {
		locationLoading: model.layers.locationLoading,
		assetsUrl: model.app.config.GH_ASSETS_CLOUDFRONT_URL,
		availableCategories,
		featureAtLocation: model.layers.featureAtLocation || null,
		huntUnitsAtLocation: model.layers.huntUnitsAtLocation || [],
		huntUnitsLoading: model.layers.huntUnitsLoading,
		layersAtLocationData,
		hideCoords: model.app.hideCoords,
		stateName: resolvedStateName,
		userProfile: model.user.profile,
	}
})(AboutLocationSidebar)
